/* Pure helpers for the Suggested Tasks page: words and numbers only, no React, so node can test them. */

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const MAX_HOURS = 1000;

function plural(count, word) {
  return `${count} ${word}${count === 1 ? "" : "s"} ago`;
}

/** "5 minutes ago". Empty when the time is missing or unreadable. */
export function timeAgo(value, now = new Date()) {
  if (!value) return "";
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return "";
  const elapsed = now.getTime() - then.getTime();
  if (elapsed < MINUTE) return "just now";
  if (elapsed < HOUR) return plural(Math.floor(elapsed / MINUTE), "minute");
  if (elapsed < DAY) return plural(Math.floor(elapsed / HOUR), "hour");
  return plural(Math.floor(elapsed / DAY), "day");
}

/** What a person typed into the hours box: nothing (keep the proposal's hours) or a sensible number. */
export function parseHours(text) {
  const clean = String(text ?? "").trim();
  if (!clean) return { ok: true, value: null };
  const value = Number(clean);
  if (!/^\d+(\.\d+)?$/.test(clean) || !Number.isFinite(value) || value <= 0 || value > MAX_HOURS) {
    return { ok: false, error: `Enter the hours as a number above zero, up to ${MAX_HOURS}.` };
  }
  return { ok: true, value };
}

const TRY_AGAIN = "That did not go through. Try again in a moment.";

/** One plain sentence for a failed Approve, Reject or list, chosen by the server's closed code. */
export function decisionError(error) {
  const code = error?.payload?.code;
  if (code === "already_handled") return "Someone else already handled this suggestion.";
  if (code === "no_approver") return "Only a team member can decide on suggestions.";
  if ((code === "invalid" || code === "not_found") && error?.payload?.detail) return error.payload.detail;
  if (error?.status === 422 && error?.payload?.detail) return error.payload.detail;
  if (error?.status === 403) return "You do not have access to decide on suggestions.";
  return TRY_AGAIN;
}

const SHORT_FACT = 40;

/** "in_progress" -> "In progress": the server sends a task's stored status word as it is kept. */
function statusWords(value) {
  const text = String(value).replace(/_/g, " ").trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * The facts a card shows, made easy to read: a "Task" fact that only repeats the title is dropped,
 * a stored status word becomes plain words, the end-of-day "Items" become a short list, and long
 * values take the full row instead of a squeezed column.
 */
export function readableFacts(facts, title = "") {
  return (facts || []).flatMap((fact) => {
    const value = String(fact.value ?? "");
    if (fact.label === "Task" && title.includes(value)) return [];
    if (fact.label === "Items") {
      const items = value.split(";").map((part) => part.trim()).filter(Boolean);
      return [{ label: fact.label, value, items: items.length > 1 ? items : [], wide: true }];
    }
    const shown = /status$/i.test(fact.label) && /^[a-z]+(_[a-z]+)*$/.test(value) ? statusWords(value) : value;
    return [{ label: fact.label, value: shown, items: [], wide: shown.length > SHORT_FACT }];
  });
}

/**
 * The signed-in data, with the Suggested Tasks page readable when the server says this person has
 * something to decide there (the menu answer). The server decides every request on its own; this
 * only lets the menu and the route show what it already allows. The input is never changed.
 */
export function withSuggestedTasksAccess(data, serverSaysShow) {
  const me = data?.me;
  if (!serverSaysShow || !me) return data;
  const grid = me.pageAccess || {};
  if ((grid.proposals || []).includes("read")) return data;
  return { ...data, me: { ...me, pageAccess: { ...grid, proposals: [...(grid.proposals || []), "read"] } } };
}

/**
 * Groups the loaded cards for the page. Cards that share a helper and a bundle id (a sprint plan's tasks)
 * become one block placed where the first of them was; every other card stays a block of its own. A bundle
 * of one card is not worth a frame, so it stays single. Order never changes after this: handled cards fold
 * in place instead of moving.
 */
export function groupByBundle(items) {
  const byKey = new Map();
  for (const item of items || []) {
    if (!item.bundle_id) continue;
    const key = `${item.helper}\u0000${item.bundle_id}`;
    byKey.set(key, [...(byKey.get(key) || []), item]);
  }
  const blocks = [];
  const placed = new Set();
  for (const item of items || []) {
    const key = item.bundle_id ? `${item.helper}\u0000${item.bundle_id}` : "";
    const members = key ? byKey.get(key) : null;
    if (!members || members.length < 2) {
      blocks.push({ kind: "single", key: `card-${item.id}`, item });
    } else if (!placed.has(key)) {
      placed.add(key);
      blocks.push({
        kind: "bundle", key, helper: item.helper, bundleId: item.bundle_id,
        title: members.find((m) => m.bundle_title)?.bundle_title || "Suggested together", items: members,
      });
    }
  }
  return blocks;
}

/** How one card folds away after a bundle decision, from the server's per-card result. */
export function cardOutcome(verb, result) {
  if (result.status === "approved") {
    return { kind: "Approved", text: result.task_id ? `Approved · task #${result.task_id} created` : "Approved" };
  }
  if (result.status === "rejected") return { kind: "Rejected", text: "Rejected" };
  if (result.status === "failed") {
    return { kind: "Failed", text: `Could not be ${verb === "approve" ? "created" : "rejected"}${result.detail ? `: ${result.detail}` : ""}` };
  }
  return { kind: "Gone", text: result.detail || "Someone else already handled this suggestion." };
}

function count(number, word) {
  return `${number} ${word}${number === 1 ? "" : "s"}`;
}

/** One calm sentence about a whole-bundle decision. Says plainly when some did not go through. */
export function bundleSummary(verb, answer) {
  const done = verb === "approve" ? answer.approved : answer.rejected;
  const parts = [];
  if (done > 0) parts.push(verb === "approve" ? `${count(done, "task")} created` : `${count(done, "suggestion")} rejected`);
  if (answer.failed > 0) parts.push(`${count(answer.failed, "task")} could not be ${verb === "approve" ? "created" : "rejected"}`);
  if (answer.gone > 0) parts.push(`${answer.gone} already handled`);
  return parts.length ? `${parts.join(", ")}.` : "Nothing to do.";
}
